import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';

const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);
const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
function coerceObject(value = null) {
  return (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
}

function normalizeText(value) {
  return String(value || '').trim();
}

function normalizeList(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeNumber(value, fallback = 0) {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
}

function resolveLatestTrackPayload(timelineItems = []) {
  const items = Array.isArray(timelineItems) ? timelineItems : [];
  const latest = [...items]
    .reverse()
    .find((entry) => normalizeText(entry?.type) === PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE
      && (
        Object.keys(coerceObject(entry?.payload?.workflow_tracker)).length > 0
        || Object.keys(coerceObject(entry?.payload?.track_result)).length > 0
      ));
  if (!latest) {
    return null;
  }
  return {
    roundKey: normalizeText(latest.roundKey),
    workflowTracker: coerceObject(latest.payload.workflow_tracker),
    trackResult: coerceObject(latest.payload.track_result),
  };
}

function resolveCurrentStep(trackResult = {}) {
  const steps = normalizeList(trackResult.steps);
  const currentStepId = normalizeText(trackResult.current_step_id);
  return steps.find((step) => normalizeText(step?.step_id) === currentStepId)
    || steps.find((step) => normalizeText(step?.status) === 'active')
    || null;
}

function resolveReadiness(trackResult = {}) {
  const readiness = coerceObject(trackResult.readiness);
  return {
    score: normalizeNumber(readiness.readiness_score, normalizeNumber(trackResult.confidence, 0)),
    level: normalizeText(readiness.readiness_level),
    reason: normalizeText(readiness.reason),
    knownCount: normalizeNumber(readiness.known_count, 0),
    unknownCount: normalizeNumber(readiness.unknown_count, 0),
    documentCount: normalizeNumber(readiness.document_count, 0),
    blockingGaps: normalizeList(readiness.blocking_gaps).map(normalizeText).filter(Boolean),
    nonBlockingGaps: normalizeList(readiness.non_blocking_gaps).map(normalizeText).filter(Boolean),
  };
}

function resolveTrackQuestion(trackResult = {}) {
  const question = coerceObject(trackResult.next_question);
  const text = normalizeText(question.text || question.question_text);
  if (!text) {
    return null;
  }
  return {
    text,
    target: normalizeText(question.target || question.field_key),
    reason: normalizeText(question.reason),
    readinessScore: normalizeNumber(question.readiness_score, 0),
    readinessLevel: normalizeText(question.readiness_level),
  };
}

function resolveTrackActions(trackResult = {}) {
  return normalizeList(trackResult.next_actions)
    .map((action) => coerceObject(action))
    .filter((action) => normalizeText(action.label))
    .map((action) => ({
      actionType: normalizeText(action.action_type),
      label: normalizeText(action.label),
      reason: normalizeText(action.reason),
      capability: normalizeText(action.capability),
      score: normalizeNumber(action.score, 0),
      readinessScore: normalizeNumber(action.readiness_score, 0),
      readinessLevel: normalizeText(action.readiness_level),
    }));
}

function resolveTrackDecision(trackResult = {}) {
  const decision = coerceObject(trackResult.decision);
  const selectedAction = coerceObject(decision.selected_action);
  if (!normalizeText(decision.current_step_id) && !normalizeText(selectedAction.label)) {
    return null;
  }
  return {
    currentStepId: normalizeText(decision.current_step_id),
    readinessScore: normalizeNumber(decision.readiness_score, 0),
    readinessLevel: normalizeText(decision.readiness_level),
    reason: normalizeText(decision.reason),
    confidence: normalizeNumber(decision.confidence, 0),
    selectedAction: normalizeText(selectedAction.label) ? {
      actionType: normalizeText(selectedAction.action_type),
      label: normalizeText(selectedAction.label),
      capability: normalizeText(selectedAction.capability),
      score: normalizeNumber(selectedAction.score, 0),
    } : null,
  };
}

function resolveTrackerItems(workflowTracker = {}) {
  return normalizeList(workflowTracker.items)
    .map((item) => coerceObject(item))
    .filter((item) => normalizeText(item.item_id))
    .map((item) => {
      const readiness = coerceObject(item.readiness);
      return {
        itemId: normalizeText(item.item_id),
        capability: normalizeText(item.capability),
        label: normalizeText(item.label),
        role: normalizeText(item.role),
        state: normalizeText(item.state),
        dependsOn: normalizeList(item.depends_on).map(normalizeText).filter(Boolean),
        readiness: {
          level: normalizeText(readiness.level),
          score: normalizeNumber(readiness.score, 0),
          reasonCodes: normalizeList(readiness.reason_codes).map(normalizeText).filter(Boolean),
        },
      };
    });
}

function resolveTrackerActions(workflowTracker = {}) {
  return normalizeList(workflowTracker.next_actions)
    .map((action) => coerceObject(action))
    .filter((action) => normalizeText(action.action_key))
    .map((action) => ({
      actionKey: normalizeText(action.action_key),
      label: normalizeText(action.label),
      targetItemId: normalizeText(action.target_item_id),
      status: normalizeText(action.status),
    }));
}

function resolveCurrentTrackerItem(workflowTracker = {}) {
  const items = resolveTrackerItems(workflowTracker);
  const primaryItemId = normalizeText(workflowTracker.primary_item_id);
  return items.find((item) => ['running', 'waiting_user', 'ready'].includes(item.state))
    || items.find((item) => item.itemId === primaryItemId)
    || null;
}

function buildWorkflowTrackerView(latest) {
  const tracker = latest.workflowTracker;
  const objective = coerceObject(tracker.objective);
  const currentItem = resolveCurrentTrackerItem(tracker);
  const title = normalizeText(objective.label);
  if (!title && !normalizeText(Object(currentItem).label)) {
    return null;
  }
  return {
    roundKey: latest.roundKey,
    trackId: normalizeText(tracker.tracker_id),
    trackKey: normalizeText(objective.capability),
    title: title || normalizeText(objective.capability),
    source: normalizeText(tracker.source),
    goal: title,
    status: normalizeText(tracker.status),
    contractVersion: normalizeText(tracker.contract_version),
    primaryItemId: normalizeText(tracker.primary_item_id),
    currentStepId: normalizeText(Object(currentItem).itemId),
    currentStepTitle: normalizeText(Object(currentItem).label),
    items: resolveTrackerItems(tracker),
    known: [],
    unknowns: [],
    readiness: {
      score: normalizeNumber(Object(Object(currentItem).readiness).score, 0),
      level: normalizeText(Object(Object(currentItem).readiness).level),
      reason: '',
      knownCount: 0,
      unknownCount: 0,
      documentCount: 0,
      blockingGaps: normalizeList(tracker.reason_codes).map(normalizeText).filter(Boolean),
      nonBlockingGaps: [],
    },
    decision: null,
    nextQuestion: null,
    nextActions: resolveTrackerActions(tracker),
  };
}

export function selectActiveTrackView({ timelineItems } = {}) {
  const latest = resolveLatestTrackPayload(timelineItems);
  if (!latest) {
    return null;
  }
  if (Object.keys(latest.workflowTracker).length > 0) {
    return buildWorkflowTrackerView(latest);
  }
  const currentStep = resolveCurrentStep(latest.trackResult);
  const title = normalizeText(latest.trackResult.title);
  const trackKey = normalizeText(latest.trackResult.track_key);
  const currentStepTitle = normalizeText(Object(currentStep).title);
  if (everyTruthy(() => (!title), () => (!trackKey), () => (!currentStepTitle))) {
    return null;
  }
  return {
    roundKey: latest.roundKey,
    trackId: normalizeText(latest.trackResult.track_id),
    trackKey,
    title: firstTruthy(() => (title), () => (trackKey)),
    source: normalizeText(latest.trackResult.source),
    goal: normalizeText(latest.trackResult.goal),
    currentStepId: normalizeText(firstTruthy(() => (latest.trackResult.current_step_id), () => (Object(currentStep).step_id))),
    currentStepTitle,
    known: normalizeList(latest.trackResult.known).map(normalizeText).filter(Boolean),
    unknowns: normalizeList(latest.trackResult.unknowns).map(normalizeText).filter(Boolean),
    readiness: resolveReadiness(latest.trackResult),
    decision: resolveTrackDecision(latest.trackResult),
    nextQuestion: resolveTrackQuestion(latest.trackResult),
    nextActions: resolveTrackActions(latest.trackResult),
  };
}

export default selectActiveTrackView;
